import styled from "styled-components";
import { Card } from "../../../components/ui/card/card.component";
import { Spacer } from "../../../components/ui/spacer/spacer.component";
import { Row } from "../../../components/ui/grid/row.component";
import { Pill } from "../../../components/ui/pill/pill.component";
import { colors } from "../../../theme/colors";
import { TextButton } from "../../../components/ui/text-button/text-button.component";
import { Bubble } from "../../../components/ui/bubble/bubble.component";
import { useNavigate } from "react-router-dom";
import { FilmProfile } from "../../../models/film-profile.model";
import { desluggify } from "../../../utils/functions/string/desluggify";
import { valueToLabel } from "../../../utils/functions/misc/valueToLabel";
import { mediaUrl } from "../../../utils/functions/media/mediaUrl";
import { episodeDurationParts } from "../../series/utils/duration";
import { useAtomValue } from "jotai";
import { masterAtom } from "../../../atoms/masters.atom";

type PosterImage = { aspect_ratio?: string | null; image?: string | null };

type SeriesSeason = {
  season_image?: PosterImage[];
  episodes?: { duration?: unknown; episode_image?: PosterImage[] }[];
};

const HORIZONTAL_RATIOS = new Set(["16_9", "4_3", "2_39_1", "2.39_1"]);

function posterRows(
  project: FilmProfile & { series_image?: PosterImage[]; seasons?: SeriesSeason[] },
  kind: "film" | "series"
): PosterImage[] {
  if (kind !== "series") return project.film_image ?? [];
  return [
    ...(project.series_image ?? []),
    ...(project.seasons ?? []).flatMap((season) => [
      ...(season.season_image ?? []),
      ...(season.episodes ?? []).flatMap((episode) => episode.episode_image ?? []),
    ]),
  ];
}

function runtimeParts(
  project: FilmProfile & { seasons?: SeriesSeason[] },
  kind: "film" | "series"
): [string, string] {
  if (kind === "series") {
    let totalSeconds = 0;
    for (const season of project.seasons ?? []) {
      for (const episode of season.episodes ?? []) {
        const parts = episodeDurationParts(episode.duration);
        const minutes = Number(parts.mm || 0);
        const seconds = Number(parts.ss || 0);
        if (!Number.isNaN(minutes) && !Number.isNaN(seconds)) {
          totalSeconds += minutes * 60 + seconds;
        }
      }
    }
    if (totalSeconds > 0) {
      return [
        String(Math.floor(totalSeconds / 60)),
        String(totalSeconds % 60).padStart(2, "0"),
      ];
    }
  }
  const [mm, ss] = (project.duration as string)?.split(":")?.slice(1) ?? ["0", "0"];
  return [String(Number(mm) || 0), String(ss ?? "0").padStart(2, "0")];
}

export const Project = ({
  flex = false,
  fluidGrid = false,
  kind = "film",
  project,
}: {
  flex?: boolean;
  /** Listing grids: stretch cards with container (CSS grid); omit on horizontal carousels. */
  fluidGrid?: boolean;
  kind?: "film" | "series";
  project: FilmProfile;
}) => {
  const master = useAtomValue(masterAtom);

  const navigate = useNavigate();

  const seriesProject = project as FilmProfile & {
    series_image?: PosterImage[];
    seasons?: SeriesSeason[];
  };
  const [mm, ss] = runtimeParts(seriesProject, kind);
  const thumbnail =
    mediaUrl(
      [...posterRows(seriesProject, kind)].reverse().find((image) =>
        HORIZONTAL_RATIOS.has(image.aspect_ratio ?? "")
      )?.image
    ) || "/images/ui/bg-placeholder.png";

  return (
    <Main
      $fluidGrid={fluidGrid}
      style={flex ? { flex: 1 } : {}}
      onClick={() =>
        navigate(
          `/${kind === 'series' ? "s" : "f"}/${project.custom_name}${project.status === "draft" ? "/edit?draft=true" : ""
          }`
        )
      }
    >
      {/* <Bubble>2</Bubble> */}

      <Thumbnail src={thumbnail} />
      <Spacer size={10} direction="top">
        <Title>{project.title}</Title>
        <Duration>
          {mm}m {ss}s •{" "}
          {valueToLabel("genre", (project.genre ?? [])[0], master) ??
            "No genre"}{" "}
          •{" "}
          {kind === "series"
            ? "Series"
            : valueToLabel("film_category", project.category, master) ??
              "No category"}
        </Duration>
        <Spacer direction="bottom" size={5} />
        <Row style={{ width: "100%", justifyContent: "space-between" }}>
          <Pill
            $pulse
            style={{ backgroundColor: colors.complement.hex, fontWeight: 600 }}
          >
            {desluggify(project.status)}
          </Pill>
          <TextButton
            onClick={() => null}
            color={colors.isDark ? "complement" : "primary"}
          >
            {project.is_distributed
              ? "View Details"
              : project.status === "published"
                ? "Submit for Distribution"
                : "Complete and Submit"}
          </TextButton>
        </Row>
      </Spacer>
    </Main>
  );
};

const Main = styled(Card)<{ $fluidGrid?: boolean }>`
  position: relative;
  padding: 1.2rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  border-radius: 0.75rem;
  cursor: pointer;
  min-height: 0;
  height: auto;

  ${({ $fluidGrid }) =>
    $fluidGrid
      ? `
    min-width: 0;
    width: 100%;
    max-width: none;
  `
      : `
    min-width: min(100%, 27rem);
    max-width: min(100%, 37.5rem);
  `}

  @media only screen and (max-width: 48em) {
    ${({ $fluidGrid }) => (!$fluidGrid ? `flex: 1;` : "")}
  }
`;

const Thumbnail = styled.img`
  width: 100%;
  border-radius: 0.5rem;
`;

const Title = styled.h3`
  font-size: 1.8rem;
  font-weight: 600;
`;

const Duration = styled.span`
  font-size: 1.2rem;
  font-weight: 400;
`;
